import { useMemo, useState } from 'react';
import { type ColumnDef, getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { DataTablePagination } from 'components/blocks/data-table/data-table-pagination';
import { cn, formatDate, parseBackendUtc } from 'lib/utils';
import { useSubscriptionInvoices } from 'features/subscription/hooks/use-seats';
import type { SubscriptionInvoice } from 'features/subscription/services/seat-service';
import { formatMinor } from 'features/subscription/utils/seat-pools';
import { useInvoices } from '../hooks/use-credits';

type InvoiceTab = 'seats' | 'credits';

interface InvoiceRow {
  id: string;
  date: Date;
  description: string;
  number: string;
  amountMinor: number;
  currencyCode: string;
  status: { label: string; isPaid: boolean };
}

const NO_COLUMNS: ColumnDef<InvoiceRow>[] = [];

const headClass = 'h-11 whitespace-nowrap text-sm font-medium text-grey-600 dark:text-muted-foreground';
const cellClass = 'whitespace-nowrap py-5 text-sm';

/**
 * What a Utilities document's status means for the money, as the Utilities invoices page says it.
 * Its ledger only holds documents for charges that went through, so an "Issued" invoice reads as
 * Paid. A trial invoice was never charged and a credit note goes the other way, so neither is "Paid".
 */
const seatStatus = (invoice: SubscriptionInvoice, t: TFunction) => {
  if (invoice.documentType === 'TrialInvoice') {
    return { label: t('INVOICE_STATUS_NO_PAYMENT_DUE', 'No payment due'), isPaid: false };
  }
  if (invoice.documentType === 'CreditNote') {
    return { label: t('INVOICE_STATUS_CREDITED', 'Credited'), isPaid: false };
  }
  if (invoice.status === 'Refunded') {
    return { label: t('INVOICE_STATUS_REFUNDED', 'Refunded in full'), isPaid: false };
  }
  if (invoice.status === 'PartiallyRefunded') {
    return { label: t('INVOICE_STATUS_PARTIALLY_REFUNDED', 'Partially refunded'), isPaid: false };
  }
  return { label: t('INVOICE_STATUS_PAID', 'Paid'), isPaid: true };
};

/** Figma 10827:24664: seat invoices from Utilities' document ledger, credit invoices from the credits API. */
export const InvoicesTable = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<InvoiceTab>('seats');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const seatInvoices = useSubscriptionInvoices();
  const creditInvoices = useInvoices();
  const isLoading = tab === 'seats' ? seatInvoices.isLoading : creditInvoices.isLoading;

  // Memoized because the table resets its page whenever `data` is a new array, which would loop.
  const rows = useMemo<InvoiceRow[]>(() => {
    if (tab === 'credits') {
      // The credits API invoices a top-up only once it is paid.
      const paid = { label: t('INVOICE_STATUS_PAID', 'Paid'), isPaid: true };
      return (creditInvoices.data?.items ?? []).map((invoice) => ({
        id: invoice.id,
        date: parseBackendUtc(invoice.createdDate),
        description: t('INVOICE_CREDITS_TOP_UP', 'Credits top-up'),
        number: invoice.number,
        amountMinor: invoice.grossMinor,
        currencyCode: invoice.currencyCode,
        status: paid,
      }));
    }
    // Utilities answers newest first.
    return (seatInvoices.data ?? []).map((invoice) => ({
      id: invoice.documentId,
      date: parseBackendUtc(invoice.issuedAtUtc),
      description: t('INVOICE_SEATS_PERIOD', {
        period: parseBackendUtc(invoice.periodStartUtc ?? invoice.issuedAtUtc).toLocaleDateString([], { month: 'long' }),
        defaultValue: 'Seats · {{period}}',
      }),
      number: invoice.documentNumber,
      amountMinor: Number(invoice.amounts?.totalMinor ?? 0),
      currencyCode: invoice.currencyCode,
      status: seatStatus(invoice, t),
    }));
  }, [tab, seatInvoices.data, creditInvoices.data, t]);

  // Only used for paging, so the shared pagination control can drive it.
  const table = useReactTable({
    data: rows,
    columns: NO_COLUMNS,
    state: { pagination },
    onPaginationChange: setPagination,
    // The tab buttons reset the page themselves.
    autoResetPageIndex: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const tabs: { id: InvoiceTab; label: string }[] = [
    { id: 'seats', label: t('SEATS', 'Seats') },
    { id: 'credits', label: t('CREDITS', 'Credits') },
  ];

  return (
    <section className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-medium leading-heading text-grey-950 dark:text-foreground">
          {t('INVOICES', 'Invoices')}
        </h2>
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t('FILTER_INVOICES', 'Filter invoices')}>
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={tab === item.id}
              onClick={() => {
                setTab(item.id);
                setPagination((current) => ({ ...current, pageIndex: 0 }));
              }}
              className={cn(
                'h-8 rounded-full border px-3 text-xs font-medium leading-heading transition-colors',
                tab === item.id
                  ? 'border-grey-900 bg-grey-900 text-true-white dark:border-true-white dark:bg-true-white dark:text-grey-950'
                  : 'border-grey-100 bg-true-white text-grey-900 hover:bg-grey-50 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted'
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-section border border-grey-100 bg-true-white dark:border-border dark:bg-card">
        {!isLoading && !rows.length ? (
          <p className="p-6 text-center text-sm text-grey-500 dark:text-muted-foreground">
            {t('NO_INVOICES_FOR_FILTER', 'No invoices of this type yet.')}
          </p>
        ) : (
          <Table>
            <TableHeader className="bg-grey-50 dark:bg-muted [&_tr]:border-grey-100 dark:[&_tr]:border-border">
              <TableRow className="hover:bg-transparent">
                <TableHead className={headClass}>{t('DATE', 'Date')}</TableHead>
                {/* Phones keep date, amount and status; the rest join from sm up. */}
                <TableHead className={cn(headClass, 'hidden sm:table-cell')}>{t('DESCRIPTION', 'Description')}</TableHead>
                <TableHead className={cn(headClass, 'hidden md:table-cell')}>{t('INVOICE', 'Invoice')}</TableHead>
                <TableHead className={cn(headClass, 'text-right')}>{t('AMOUNT', 'Amount')}</TableHead>
                <TableHead className={headClass}>{t('STATUS', 'Status')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading
                ? [0, 1, 2].map((index) => (
                    <TableRow key={index} className="border-grey-100 dark:border-border">
                      <TableCell colSpan={5} className="py-5">
                        <Skeleton className="h-5 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                : table.getRowModel().rows.map(({ original: row }) => (
                    <TableRow key={row.id} className="border-grey-100 hover:bg-transparent dark:border-border">
                      <TableCell className={cn(cellClass, 'text-grey-600 dark:text-muted-foreground')}>
                        {formatDate(row.date)}
                      </TableCell>
                      <TableCell className={cn(cellClass, 'hidden text-grey-950 dark:text-foreground sm:table-cell')}>
                        {row.description}
                      </TableCell>
                      <TableCell className={cn(cellClass, 'hidden text-grey-600 dark:text-muted-foreground md:table-cell')}>
                        {row.number}
                      </TableCell>
                      <TableCell className={cn(cellClass, 'text-right tabular-nums text-grey-950 dark:text-foreground')}>
                        {formatMinor(row.amountMinor, row.currencyCode)}
                      </TableCell>
                      <TableCell className={cellClass}>
                        <span
                          className={cn(
                            'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium leading-heading',
                            row.status.isPaid
                              ? 'bg-success-50 text-success-700 dark:bg-success-700/20 dark:text-success-50'
                              : 'bg-grey-100 text-grey-700 dark:bg-muted dark:text-muted-foreground'
                          )}
                        >
                          {row.status.label}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        )}
      </div>

      {rows.length > 0 && <DataTablePagination table={table} showSelectedRowContent={false} />}
    </section>
  );
};
